import React, { useContext } from "react"
import { Box } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "../../../../mp-core/redux/store"
import { logOutAction } from "../../../../mp-core/redux/modules/users"
import { FeatureFlagContext } from "../../../../mp-core/FeatureFlagProvider"
import { checkAgreement } from "../../../../../services/ready-services/agreements/agreementQueryResolvers"
import { saveAgreement } from "../../../../../services/ready-services/agreements/agreementMutationResolvers"
import { useRequiredAgreement } from "../../../../../../../../packages/mp-shared-components/src/legal/useRequiredAgreement"
import { AOP_DOCUMENT, TERMS_DOCUMENT } from "../../../../../../../../packages/mp-shared-components/src/legal/documents"
import { useDelayedAnnounce } from "../../../../../../../../packages/mp-shared-components/src/generic/AnnounceProvider/AnnounceProvider"
import Loader from "../../../../mp-core/components/Loader"
import { useStyles as useAppStyles } from "../../../../../pages/styles"
import TermsOfServiceModal from "./TermsOfServiceModal"

// Ready blocks AOP users: try the current MasteryPrep Terms, then fall back to the
// legacy AOP terms before showing the blocking error.
const AGREEMENT_DOCUMENTS = [TERMS_DOCUMENT, AOP_DOCUMENT]

const AgreementLoading: React.FC = () => {
  const { classes } = useAppStyles()
  useDelayedAnnounce("Loading application", 500)
  return (
    <Box
      role="region"
      aria-label="Loading application"
      aria-busy="true"
      data-automation-id="acceptance-agreement-loading"
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      className={classes.appWideBackgroundColor}
    >
      <Box aria-hidden="true"><Loader /></Box>
    </Box>
  )
}

const RequiredTerms: React.FC<React.PropsWithChildren<{ onLogout: () => void }>> = ({ children, onLogout }) => {
  const { status, agreement, error, saving, agree, retry } = useRequiredAgreement({ checkAgreement, saveAgreement, documents: AGREEMENT_DOCUMENTS })
  if (status === "accepted") return <>{children}</>
  if (status === "checking") return <AgreementLoading />

  return (
    <TermsOfServiceModal
      agreementContent={agreement?.content || null}
      onAgree={agree}
      onLogout={onLogout}
      onRetry={retry}
      error={error}
      saveLoading={saving}
    />
  )
}

const TermsOfServiceProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>()
  const userId = useSelector((state: RootState) => state.users.testUser?.id)
  const isAuthed = useSelector((state: RootState) => state.users.isAuthed)
  const { featureFlags, featureFlagsLoading } = useContext(FeatureFlagContext)
  const logout = () => { dispatch(logOutAction()) }

  if (!isAuthed) return <>{children}</>
  if (featureFlagsLoading || !userId) {
    return <AgreementLoading />
  }
  // Operational rollback only; the release enables this flag for every user.
  if (featureFlags?.terms_of_service === false) return <>{children}</>

  // A new identity cannot inherit a previous user's completed check or save.
  return <RequiredTerms key={userId} onLogout={logout}>{children}</RequiredTerms>
}

export default TermsOfServiceProvider
